import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AddPaymentMethodInput, TransactionListQuery } from '@teentack/shared';
import {
  addPaymentMethod,
  fetchMoneySummary,
  fetchPaymentMethods,
  fetchTransactions,
  removePaymentMethod,
  setDefaultPaymentMethod,
} from './payments.api';

export const paymentKeys = {
  all: ['payments'] as const,
  methods: ['payments', 'methods'] as const,
  transactions: (query: Partial<TransactionListQuery>) =>
    ['payments', 'transactions', query] as const,
  summary: ['payments', 'summary'] as const,
};

export function usePaymentMethods(enabled = true) {
  return useQuery({
    queryKey: paymentKeys.methods,
    queryFn: fetchPaymentMethods,
    enabled,
  });
}

export function useTransactions(query: Partial<TransactionListQuery> = {}, enabled = true) {
  return useQuery({
    queryKey: paymentKeys.transactions(query),
    queryFn: () => fetchTransactions(query),
    enabled,
  });
}

export function useMoneySummary(enabled = true) {
  return useQuery({
    queryKey: paymentKeys.summary,
    queryFn: fetchMoneySummary,
    enabled,
  });
}

export function useAddPaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddPaymentMethodInput) => addPaymentMethod(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.methods });
    },
  });
}

export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (methodId: string) => setDefaultPaymentMethod(methodId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.methods });
    },
  });
}

export function useRemovePaymentMethod() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (methodId: string) => removePaymentMethod(methodId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: paymentKeys.methods });
    },
  });
}
